import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isMobile } from 'react-device-detect'
import { UIElement } from '../../types/UIElements'
import { useContextUI } from '../../contexts/UIContext'
import { useContextMotifDetails } from '../../contexts/motifDetailsContext'
import { keywordTags } from '../../constants/tags'
import CloseButton from '../CloseButton'
import KeywordsFilterFooter from './Footer'
import KeywordColumn from './KeywordColumn'
import { getFirstHungarianLetter, getLocalizedName } from './utils'
import { KeywordsFilterWrapper, Panel, List } from './styled'
import { createGlobalStyle } from 'styled-components'

const KeywordsFilterGlobalStyle = createGlobalStyle`
  html {
    font-size: 16px;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.deviceW.sm}) {
      font-size: 14px;
    }
    
    @media (min-width: ${({ theme }) => theme.breakpoints.deviceW.md}) {
      font-size: 16px;
    }
    
    @media (min-width: ${({ theme }) => theme.breakpoints.deviceW.lg}) {
      font-size: 17px;
    }
    
    @media (min-width: ${({ theme }) => theme.breakpoints.deviceW.xxl}) {
      font-size: 18px;
    }
  }
`

const KeywordsFilter = () => {
  const { i18n } = useTranslation()
  const { elementsOpen, setElementsOpen, setOverlayVisible } = useContextUI()
  const { applyTagFilter, applyGeoFilter, applyKeywordFilter } = useContextMotifDetails()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const listRef = useRef<HTMLDivElement | null>(null)
  const dragStartXRef = useRef(0)
  const dragStartScrollLeftRef = useRef(0)
  const [isDragging, setIsDragging] = useState(false)

  useEffect(() => {
    if (elementsOpen.includes(UIElement.KEYWORDFILTER)) {
      setIsOpen(true)
      applyTagFilter(undefined)
      applyGeoFilter(undefined)
    } else {
      setIsOpen(false)
    }
  }, [elementsOpen])

  const locale = i18n.language.startsWith('en') ? 'en' : 'hu'

  const visibleKeywords = useMemo(() => {
    return [...keywordTags]
      .filter(keyword => Number.isFinite(keyword.id))
      .sort((a, b) => {
        const nameA = getLocalizedName(a, i18n.language)
        const nameB = getLocalizedName(b, i18n.language)
        return nameA.localeCompare(nameB, locale, { sensitivity: 'base' })
      })
  }, [keywordTags, i18n.language, locale])

  const keywordCountRange = useMemo(() => {
    if (!visibleKeywords.length) {
      return { minCount: 0, maxCount: 0 }
    }

    let minCount = Number.POSITIVE_INFINITY
    let maxCount = Number.NEGATIVE_INFINITY

    visibleKeywords.forEach(keyword => {
      minCount = Math.min(minCount, keyword.count)
      maxCount = Math.max(maxCount, keyword.count)
    })

    return { minCount, maxCount }
  }, [visibleKeywords])

  const groupedKeywords = useMemo(() => {
    const grouped = visibleKeywords.reduce<Record<string, typeof visibleKeywords>>(
      (acc, keyword) => {
        const localizedName = getLocalizedName(keyword, i18n.language)
        const firstLetter = getFirstHungarianLetter(localizedName || '')

        if (!acc[firstLetter]) {
          acc[firstLetter] = []
        }

        acc[firstLetter].push(keyword)
        return acc
      },
      {},
    )

    return Object.entries(grouped).sort(([a], [b]) => {
      if (a === '#') return 1
      if (b === '#') return -1
      return a.localeCompare(b, 'hu-HU', { sensitivity: 'base' })
    })
  }, [visibleKeywords, i18n.language])

  const letters = useMemo(() => groupedKeywords.map(([letter]) => letter), [groupedKeywords])

  const handleWheel: React.WheelEventHandler<HTMLDivElement> = e => {
    const list = listRef.current
    if (!list) return

    if (Math.abs(e.deltaY) > 0) {
      e.preventDefault()
      list.scrollLeft += e.deltaY
    }
  }

  const handlePointerDown: React.PointerEventHandler<HTMLDivElement> = e => {
    if (e.pointerType === 'mouse' && e.button !== 0) return
    const list = listRef.current
    if (!list) return

    setIsDragging(true)
    dragStartXRef.current = e.clientX
    dragStartScrollLeftRef.current = list.scrollLeft
  }

  const handlePointerMove: React.PointerEventHandler<HTMLDivElement> = e => {
    if (!isDragging) return
    const list = listRef.current
    if (!list) return

    const delta = e.clientX - dragStartXRef.current
    list.scrollLeft = dragStartScrollLeftRef.current - delta
  }

  const stopDragging = () => {
    setIsDragging(false)
  }

  const close = () => {
    setIsOpen(false)
    setOverlayVisible(false)
    window.history.replaceState(null, '', '/')
    setElementsOpen([UIElement.CONTROLS])
    applyKeywordFilter(undefined)
  }

  // Stable identity so the memoized keyword columns/buttons don't re-render
  const selectKeyword = useCallback(
    (keywordId: number) => {
      applyKeywordFilter(keywordId)
      setOverlayVisible(false)
      window.history.replaceState(null, '', '/')
      setElementsOpen([UIElement.CONTROLS])
      setIsOpen(false)
    },
    [applyKeywordFilter, setOverlayVisible, setElementsOpen],
  )

  const handleOutsideClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const el = e.target as HTMLElement
    if (el.classList.contains('outside')) {
      close()
    }
  }

  if (!isOpen) return null

  return (
    <>
      <KeywordsFilterGlobalStyle />
      <KeywordsFilterWrapper className="outside" onClick={handleOutsideClick}>
        <Panel>
          <List
            ref={listRef}
            $dragging={isDragging}
            onWheel={handleWheel}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={stopDragging}
            onPointerCancel={stopDragging}
            onPointerLeave={stopDragging}
          >
            {groupedKeywords.map(([letter, items]) => (
              <KeywordColumn
                key={letter}
                letter={letter}
                items={items}
                language={i18n.language}
                minCount={keywordCountRange.minCount}
                maxCount={keywordCountRange.maxCount}
                onSelectKeyword={selectKeyword}
              />
            ))}
          </List>
          {!isMobile && (
            <KeywordsFilterFooter
              listRef={listRef}
              letters={letters}
              keywordCountRange={keywordCountRange}
            />
          )}
        </Panel>
      </KeywordsFilterWrapper>
      <CloseButton onClick={close} />
    </>
  )
}

export default KeywordsFilter
